'use server';

/**
 * «Выбрать другой голос»: the narration waits on its voice (clone not ready,
 * failed, or it does not speak the story's language — node clone_wait). The
 * parent picks another voice; readiness and the language are checked again
 * and the run goes on with that voice's engine (chooseAnotherVoice).
 */
import { SessionService } from '@/contexts/authentication';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import type { ActionResult } from '@/shared/types/actions';

export async function chooseNarrationVoice(input: { storyId: string; layerId: string; voiceId: string }): Promise<ActionResult<{ ok: true }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };
    const { getOrderPlanService } = await import('../order/server');
    const view = await getOrderPlanService().viewForStory(input.storyId, familyId);
    const layer = view?.layers.find((l) => l.layerId === input.layerId && l.kind === 'narration');
    if (!layer || layer.state !== 'awaiting_user') return { success: false, error: 'Not found', code: 'VOICE_NOT_WAITING' };
    const { getJobsPool } = await import('@/shared/infrastructure/workflows/jobsDb');
    const { narrationStartKey } = await import('../narration/workflow');
    const r = await getJobsPool().query('SELECT id FROM workflow_runs WHERE start_key = $1 ORDER BY created_at DESC LIMIT 1', [narrationStartKey(layer.layerId)]);
    const runId = (r.rows[0] as { id: string } | undefined)?.id;
    if (!runId) return { success: false, error: 'Not found', code: 'VOICE_NOT_WAITING' };
    const language = String(layer.params.language ?? 'ru');
    const { narrationVoiceOf, narrationEngineConfig } = await import('../narration/voices.server');
    const voice = await narrationVoiceOf(familyId, input.voiceId);
    const { resolveEngine } = await import('../narration/engine');
    const engine = resolveEngine(voice, await narrationEngineConfig(language, voice));
    const { getNarrationProjection } = await import('../narration/server');
    const res = await getNarrationProjection().chooseAnotherVoice(runId, voice, engine, language);
    // the projection's message is English for logs; the dialog shows its own text by code
    if (!res.ok) return { success: false, error: res.message, code: 'VOICE_REFUSED' };
    return { success: true, data: { ok: true } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('chooseNarrationVoice', e) };
  }
}
